import { useEffect } from 'react'
import isEmpty from 'lodash/isEmpty'
import type { AddChatInternalNoteArgs, ChatMessage } from '@ztechapps/common.contract'
import { useError, useMutation } from '@ztechapps/common.hook'
import { isBlankHtml } from '@ztechapps/common.utils'
import { useChannelContext } from '../Channel'
import { addChatInternalNoteMutation } from '../Inbox/graphql'
import { useConversationContext } from './Context'

export type UseComposerAddNoteResult = {
  onAddNote: () => Promise<void>
  savingNote: boolean
}

// The Internal note save every Conversation mount shares. A note bypasses the channel's send: that
// path mails first and persists after with no branch that suppresses the mail, so routing a note
// through it would reach the customer. The thread is re-read so the note renders.
export const useComposerAddNote = (): UseComposerAddNoteResult => {
  const { chatId, message, setMessage, onRefresh } = useChannelContext()
  const { setSavingNote } = useConversationContext()
  const [addNote, , { loading: savingNote, error }] = useMutation<ChatMessage, AddChatInternalNoteArgs>({
    query: addChatInternalNoteMutation,
  })
  useError(error)

  // Reports the save up to ConversationProvider's `disabled`, so ChannelProvider's single
  // `status.busy` locks the editor, the wand, Attach and the template picker for its duration.
  useEffect(() => {
    setSavingNote(savingNote)
  }, [savingNote, setSavingNote])

  const onAddNote = async () => {
    if (!chatId || isBlankHtml(message)) return
    // The error toast comes from `useError`; the catch only keeps the rejection from going unhandled.
    const result = await addNote({ input: { chatId, content: message } }).catch(() => undefined)
    // A refused save keeps the draft in the editor.
    if (!result || !isEmpty(result.errors)) return
    // The composer stays in Internal note mode after a save.
    setMessage('')
    await onRefresh()
  }

  return { onAddNote, savingNote }
}
